/**
 * CharacterSystem.ts — Character creation, leveling, inventory, and traits.
 *
 * Manages character lifecycle: creation from class definition, XP gain,
 * level-up with stat point allocation, equipment management, and
 * relationship tier labels.
 */

import type { ClassId, StatName } from '@game/data/types';
import { GameState } from '@core/engine/GameState';
import type { EventSystem } from '@core/engine/EventSystem';

// ─── XP Thresholds ────────────────────────────────────────────

/** XP required to reach each level. Index 0 = level 1→2, etc. */
const XP_THRESHOLDS: number[] = [
  500,    // Level 1 → 2
  1200,   // Level 2 → 3
  2000,   // Level 3 → 4
  3000,   // Level 4 → 5
  4500,   // Level 5 → 6
  6500,   // Level 6 → 7
  9000,   // Level 7 → 8
  12000,  // Level 8 → 9
  15500,  // Level 9 → 10
  20000,  // Level 10 → 11
  25000,  // Level 11 → 12
  31000,  // Level 12 → 13
  38000,  // Level 13 → 14
  46000,  // Level 14 → 15
  55000,  // Level 15 → 16
];

/** Maximum character level. */
export const MAX_LEVEL = 16;

/** Stat points awarded per level-up. */
const STAT_POINTS_PER_LEVEL = 2;

// ─── Relationship Tiers ───────────────────────────────────────

export type RelationshipTier = 'Hostile' | 'Distrustful' | 'Neutral' | 'Friendly' | 'Allied';

const RELATIONSHIP_TIERS: { min: number; label: RelationshipTier }[] = [
  { min: 60, label: 'Allied' },
  { min: 20, label: 'Friendly' },
  { min: -20, label: 'Neutral' },
  { min: -60, label: 'Distrustful' },
  { min: -100, label: 'Hostile' },
];

// ─── CharacterSystem ──────────────────────────────────────────

export class CharacterSystem {
  private events: EventSystem;

  constructor(events: EventSystem) {
    this.events = events;
  }

  // ─── Character Creation ─────────────────────────────────

  /** Create a new character with the given class and name. */
  createCharacter(classId: ClassId, name: string, startNodeId?: string): GameState {
    const state = GameState.createNew(classId, name, startNodeId);
    state.setEventSystem(this.events);
    return state;
  }

  // ─── XP & Leveling ─────────────────────────────────────

  /** Add XP and check for level-up. Returns true if leveled up. */
  addXP(amount: number, state: GameState): boolean {
    state.modifyStat('experience_points', amount);
    return this.checkLevelUp(state);
  }

  /** Check if the character has enough XP to level up. Applies all pending level-ups. */
  checkLevelUp(state: GameState): boolean {
    let leveledUp = false;

    while (state.character.level < MAX_LEVEL) {
      const thresholdIndex = state.character.level - 1;
      if (thresholdIndex >= XP_THRESHOLDS.length) break;

      const required = XP_THRESHOLDS[thresholdIndex];
      if (state.character.xp < required) break;

      this.levelUp(state);
      leveledUp = true;
    }

    return leveledUp;
  }

  /** Apply a single level-up: increment level, add stat points, recalculate HP. */
  levelUp(state: GameState): void {
    state.character.level++;
    state.character.unspentStatPoints += STAT_POINTS_PER_LEVEL;

    // HP increase based on class constitution modifier
    const conMod = Math.floor((state.character.stats.constitution - 10) / 2);
    const hpGain = Math.max(1, 4 + conMod); // minimum 1 HP per level
    state.character.hpMax += hpGain;
    state.character.hp += hpGain; // Heal the gained HP

    this.events.emit('levelUp', {
      level: state.character.level,
      hpGain,
      unspentPoints: state.character.unspentStatPoints,
    });
  }

  /** Get the XP required for the next level-up, or undefined if max level. */
  getXPForNextLevel(state: GameState): number | undefined {
    const index = state.character.level - 1;
    if (index >= XP_THRESHOLDS.length) return undefined;
    return XP_THRESHOLDS[index];
  }

  /** Get XP progress as a fraction (0 to 1) toward next level. */
  getXPProgress(state: GameState): number {
    const required = this.getXPForNextLevel(state);
    if (required === undefined) return 1; // Max level
    const prevRequired = state.character.level >= 2
      ? XP_THRESHOLDS[state.character.level - 2]
      : 0;
    const range = required - prevRequired;
    if (range <= 0) return 1;
    return Math.min(1, (state.character.xp - prevRequired) / range);
  }

  // ─── Stat Allocation ───────────────────────────────────

  /** Allocate one unspent stat point to a stat. Returns true if successful. */
  allocateStatPoint(stat: StatName, state: GameState): boolean {
    if (state.character.unspentStatPoints <= 0) return false;

    state.character.stats[stat]++;
    state.character.unspentStatPoints--;

    this.events.emit('statAllocated', {
      stat,
      newValue: state.character.stats[stat],
      remaining: state.character.unspentStatPoints,
    });

    return true;
  }

  // ─── Equipment ─────────────────────────────────────────

  /** Equip an item from inventory to a slot. Returns the previously equipped item ID, if any. */
  equipItem(itemId: string, slot: 'weapon' | 'armor' | 'accessory', state: GameState): string | undefined {
    if (!state.hasItem(itemId)) return undefined;

    const previousItem = state.equipped[slot];

    // Remove from inventory and equip
    state.removeItem(itemId);
    state.equipped[slot] = itemId;

    // Return previous item to inventory
    if (previousItem) {
      state.addItem(previousItem);
    }

    this.events.emit('itemEquipped', { itemId, slot, previousItem });
    return previousItem;
  }

  /** Unequip an item from a slot and return it to inventory. */
  unequipItem(slot: 'weapon' | 'armor' | 'accessory', state: GameState): string | undefined {
    const itemId = state.equipped[slot];
    if (!itemId) return undefined;

    state.equipped[slot] = undefined;
    state.addItem(itemId);

    this.events.emit('itemUnequipped', { itemId, slot });
    return itemId;
  }

  // ─── Relationships ─────────────────────────────────────

  /** Get the relationship tier label for a score. */
  getRelationshipTier(score: number): RelationshipTier {
    for (const tier of RELATIONSHIP_TIERS) {
      if (score >= tier.min) return tier.label;
    }
    return 'Hostile';
  }
}
